import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { fetchOrgNodeTypes, fetchOrgTree, type OrgNode, type OrgNodeType } from '@/features/org/orgApi'

export interface VoucherHoldersApi {
  /**
   * BUTUN ko'rinadigan daraxt — ota-bobolar ham.
   *
   * ⚠️ Faqat tanlanadiganlar EMAS: tanlagich tuzilmani ko'rsatishi
   * kerak ("Mazar ombori" qaysi filial ostida), aks holda omborlar
   * ildizga ko'chib, daraxt YOLG'ON ko'rinardi
   * (`useEmployeeNodes` bilan bir xil sabab).
   */
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  /** Partiya biriktirilishi mumkin bo'lganlar */
  selectable: readonly OrgNode[]
  loading: boolean
  /**
   * Ro'yxat ISHONCHLImi.
   *
   * ⚠️ `false` bo'lganda BO'SH ro'yxat "tugun yo'q" DEGANI EMAS —
   * "javob yo'q" degani.
   */
  reliable: boolean
  reason: 'ok' | 'no-permission' | 'failed'
}

/**
 * Tugun partiyani USHLAB TURA oladimi — NOMGA emas, IMKONIYATGA.
 *
 * ⚠️ `QARORLAR.md` §19.3: kodda `kind === 'WAREHOUSE'` YOZILMAYDI.
 * Administrator `BRANCH` turida `holdsStock` ni yoqsa, filial shu
 * kundan boshlab tanlovda paydo bo'ladi va bu yerda bitta qator
 * o'zgarmaydi.
 *
 * ⚠️ Ikkala imkoniyat ham qabul qilinadi va ular BOSHQA ma'noda:
 *   · `settlementParty` — "kimga berildi" (komissiya, narx daraxti);
 *   · `holdsStock`      — "qayerda yotibdi" (ombor, filial).
 * Backend ham aynan shu ikkitasini qabul qiladi
 * (`VoucherRepository.holderKind`), ya'ni ro'yxat u yerdagi shart bilan
 * bir xil — tanlanadigan, lekin backend rad etadigan tugun bo'lmaydi.
 *
 * <h2>⚠️ HOLAT BO'YICHA KESILMAYDI — 2026-08-23 da O'ZGARTIRILDI</h2>
 * Ilgari bu yerda `n.state === 'ACTIVE'` sharti turardi. Sabab yozilgan
 * edi ("yopiq omborga partiya biriktirish uni ko'rinmas qilib
 * qo'yardi") va u o'z-o'zicha to'g'ri, LEKIN oqibati o'lchanmagan edi:
 *
 *   · `AGENT`, `RESELLER` va `BOTH` turlari `is_settlement_party = TRUE`
 *     (`V202608121200`, seed jadvali), ya'ni ular ro'yxatga TUR bo'yicha
 *     KIRADI;
 *   · agent tugunlarining ko'pchiligi `ACTIVE` EMAS — `AgentSelect`
 *     javadoc'idagi o'lchov: «jonli Staging'da 29 tadan 24 tasi ACTIVE
 *     emas».
 *
 * Ya'ni shart agentlarni ro'yxatdan JIMGINA olib tashlardi va egasi
 * ko'rgan nuqson aynan shu edi: «vaucherda agent va resellerni tanlab
 * bo'lmayapti».
 *
 * ⚠️ SERVER HOLATGA QARAMAYDI. `VoucherRepository.holderKind` faqat
 * turni o'qiydi (`is_settlement_party` / `holds_stock`) — `state`
 * ustuni so'rovda umuman yo'q. Ya'ni panel serverdan QATTIQROQ edi, va
 * bu `AgentSelect` da allaqachon rad etilgan naqsh: «panelning
 * serverdan qattiqroq bo'lishi operatorni tushunarsiz to'siqqa urardi».
 *
 * ⚠️ Eski sababning O'ZI YO'QOLMADI — u ENDI OGOHLANTIRISHGA aylandi:
 * nofaol tugun ro'yxatda holati bilan ko'rinadi va tanlanganda
 * `VoucherTransferModal` oqibatini aytadi. Yashirish bilan
 * ogohlantirishning farqi shu: birinchisi savolni javobsiz qoldiradi.
 */
export function voucherHolderNodes(
  nodes: readonly OrgNode[],
  types: readonly OrgNodeType[],
): readonly OrgNode[] {
  const eligible = new Set(
    types.filter((t) => t.holdsStock || t.settlementParty).map((t) => t.code),
  )
  return nodes.filter((n) => eligible.has(n.kind))
}

/**
 * Vaucher partiyasini ko'chirish uchun tashkiliy tugunlar.
 *
 * ⚠️ MA'LUMOT `platform` DAN KELADI (`QARORLAR.md` §20.4 — "daraxt
 * bitta egada"). Kesh kaliti ham `platform` niki (`queryKeys.org.*`),
 * ya'ni Tashkiliy tuzilma sahifasi bilan BITTA kesh yozuvi
 * bo'lishiladi va ikkinchi so'rov ketmaydi.
 *
 * ⚠️ HUQUQ CHEGARASI — VA U OCHIQ AYTILADI. `GET /api/org-nodes`
 * `org.node.read` talab qiladi. Usiz so'rov UMUMAN yuborilmaydi
 * (`enabled`) va oyna "sizda ro'yxatni ko'rish huquqi yo'q" deydi —
 * 403 ni kutib, keyin uni yashirish keraksiz shovqin bo'lardi.
 */
export function useVoucherHolders(enabled = true): VoucherHoldersApi {
  const { can } = usePermissions()
  const allowed = can('org.node.read')
  const active = enabled && allowed

  const tree = useQuery({
    queryKey: queryKeys.org.tree,
    queryFn: ({ signal }) => fetchOrgTree(signal),
    enabled: active,
  })

  const types = useQuery({
    queryKey: queryKeys.org.types,
    queryFn: fetchOrgNodeTypes,
    enabled: active,
    // Tur ta'rifi kamdan-kam o'zgaradi; o'zgarganda `org.all` prefiksi
    // uni baribir bekor qiladi (`useEmployeeNodes` bilan bir xil).
    staleTime: 5 * 60_000,
  })

  const nodes = useMemo(() => tree.data?.nodes ?? [], [tree.data])
  const typeList = useMemo(() => types.data ?? [], [types.data])
  const selectable = useMemo(() => voucherHolderNodes(nodes, typeList), [nodes, typeList])

  const failed = tree.isError || types.isError
  const loading = active && (tree.isPending || types.isPending)

  return {
    nodes,
    types: typeList,
    selectable,
    loading,
    reliable: active && !failed && !loading,
    reason: !allowed ? 'no-permission' : failed ? 'failed' : 'ok',
  }
}
